import { t } from 'i18next'
import parsePhoneNumber from 'libphonenumber-js/max'
import { isValidNumberType } from 'Utils/phoneValidation'
import {
  documentTypeIds,
  regexCellPhone,
  regexEmail,
  regexGenderId,
  regexNT,
  regexPanamaId,
  regexPassport,
  regexPhone,
  regexRUC,
  telephoneTypeIds,
} from 'Utils/constants'

export const useValidations = () => {
  const validateDocument = ({ values, errors }) => {
    const documentTypeId = values.documentTypeId?.toLowerCase()
    const isLegalDocument = values.generalStore

    const documentErrorMessage = isLegalDocument
      ? {
          [documentTypeIds.legal.identificationCard]: t(
            'CREATE_CLIENT_T.INTRODUCE_VALID_IDENTIFICATION'
          ),
          [documentTypeIds.legal.NT]: t(
            'CREATE_CLIENT_T.INTRODUCE_VALID_NT_FORMAT'
          ),
          [documentTypeIds.legal.NTGovernment]: t(
            'CREATE_CLIENT_T.INTRODUCE_VALID_NT_INFO'
          ),
          [documentTypeIds.legal.RUC]: t(
            'CREATE_CLIENT_T.INTRODUCE_VALID_RUC_NAME'
          ),
        }[documentTypeId]
      : {
          [documentTypeIds.natural.identificationCard]: t(
            'CREATE_CLIENT_T.INTRODUCE_VALID_IDENTIFICATION'
          ),
          [documentTypeIds.natural.passport]: t(
            'CREATE_CLIENT_T.INTRODUCE_VALID_PASSPORT'
          ),
          [documentTypeIds.natural.NT]: t(
            'CREATE_CLIENT_T.INTRODUCE_VALID_NT_FORMAT'
          ),
        }[documentTypeId]

    const naturalDVValidation =
      !isLegalDocument &&
      documentTypeId === documentTypeIds.natural.NT &&
      !values.DV
    const legalDVValidation =
      isLegalDocument &&
      (documentTypeId === documentTypeIds.legal.RUC ||
        documentTypeId === documentTypeIds.legal.identificationCard ||
        documentTypeId === documentTypeIds.legal.NT ||
        documentTypeId === documentTypeIds.legal.NTGovernment) &&
      !values.dv
    const naturalNationalityValidation =
      !isLegalDocument &&
      documentTypeId === documentTypeIds.natural.passport &&
      !values.nationalityId

    if (naturalDVValidation) errors.DV = t('CREATE_CLIENT_T.INVALID_DV')
    if (legalDVValidation) errors.dv = t('CREATE_CLIENT_T.INVALID_DV')
    if (naturalNationalityValidation)
      errors.nationalityId = t('CREATE_CLIENT_T.SELECT_NATIONALITY')

    const idNumber = isLegalDocument ? values.ruc : values.idNumber

    if (!idNumber) {
      isLegalDocument
        ? (errors.ruc = documentErrorMessage)
        : (errors.idNumber = documentErrorMessage)
    } else {
      const documentTypeValidation = {
        [documentTypeIds.natural.identificationCard]: regexPanamaId,
        [documentTypeIds.natural.passport]: regexPassport,
        [documentTypeIds.natural.NT]: regexNT,
        [documentTypeIds.legal.identificationCard]: regexPanamaId,
        [documentTypeIds.legal.NT]: regexNT,
        [documentTypeIds.legal.NTGovernment]: regexNT,
        [documentTypeIds.legal.RUC]: regexRUC,
      }[documentTypeId]

      if (documentTypeValidation && !documentTypeValidation.test(idNumber)) {
        isLegalDocument
          ? (errors.ruc = documentErrorMessage)
          : (errors.idNumber = documentErrorMessage)
      }
    }
  }

  const validateEmail = ({ values, errors }) => {
    const email = values.generalStore
      ? values.generalStore[0].generalEmail[0]?.address
      : values.generalEmail[0]?.address

    if (!email) {
      errors.generalEmail = [
        { address: t('CREATE_CLIENT_T.INTRODUCE_VALID_EMAIL') },
      ]
    } else if (!regexEmail.test(email)) {
      errors.generalEmail = [
        { address: t('CREATE_CLIENT_T.INTRODUCE_VALID_EMAIL') },
      ]
    }
  }
  const validateAddress = ({ values, errors }) => {
    const hasCountryId = values?.countryId
    const hasProvinceId = values?.provinceId
    const hasDistrictId = values?.districtId
    const hasTownShipId = values?.townShipId
    const hasAddressDetail = values?.addressDetail?.trim()
    const hasAddressStreet = values?.addressStreet?.trim()
    if (!hasAddressDetail) {
      errors.addressDetail = t('CREATE_CLIENT_T.INTRODUCE_VALID_ADDRESS_DETAIL')
    }
    if (!hasAddressStreet) {
      errors.addressStreet = t('CREATE_CLIENT_T.INTRODUCE_VALID_STREET')
    }
    if (!hasCountryId) {
      errors.countryId = t('CREATE_CLIENT_T.SELECT_COUNTRY')
    }
    if (!hasProvinceId) {
      errors.provinceId = t('CREATE_CLIENT_T.SELECT_PROVINCE')
    }
    if (!hasDistrictId) {
      errors.districtId = t('CREATE_CLIENT_T.SELECT_DISTRICT')
    }
    if (!hasTownShipId) {
      errors.townShipId = t('CREATE_CLIENT_T.SELECT_TOWNSHIP')
    }

    const phoneNumber = values?.addressPhoneNumber?.trim()
    const phoneCountryCode = values?.addressPhoneCountryCode || 'PA'
    const phoneTelephoneTypeId = values?.telephoneTypeId
    const phoneErrorMessage = t('ADDRESS_FORM_T.ERROR_PHONE')
    if (!phoneNumber) {
      errors.addressPhoneNumber = phoneErrorMessage
    } else {
      try {
        const parsed = parsePhoneNumber(phoneNumber, phoneCountryCode)
        if (!parsed?.isValid()) {
          errors.addressPhoneNumber = phoneErrorMessage
        } else {
          const numberType = parsed.getType()
          if (
            !numberType ||
            !isValidNumberType(numberType, phoneTelephoneTypeId?.toUpperCase())
          ) {
            errors.addressPhoneNumber = phoneErrorMessage
          }
        }
      } catch {
        errors.addressPhoneNumber = phoneErrorMessage
      }
    }
  }
  const validateGenderId = ({ values, errors }) => {
    const genderId = values?.genderId
    if (!genderId || !regexGenderId.test(genderId)) {
      errors.genderId = t('CREATE_CLIENT_T.INTRODUCE_VALID_GENDER')
    }
  }

  const validatePhoneNumber = ({ values, errors }) => {
    const phoneNumber = values.generalStore
      ? values.generalStore[0]?.generalTelephone[0]?.number
      : values.generalTelephone[0]?.number
    const telephoneType = values.generalStore
      ? values.generalStore[0]?.generalTelephone[0]?.telephoneTypeId?.toLowerCase()
      : values.generalTelephone[0]?.telephoneTypeId?.toLowerCase()
    const phoneErrorMessage =
      telephoneType === telephoneTypeIds.cellPhone
        ? t('CREATE_CLIENT_T.INTRODUCE_VALID_CELLPHONE')
        : t('CREATE_CLIENT_T.INTRODUCE_VALID_PHONE_FORMAT')

    if (!phoneNumber) {
      errors.generalTelephone = [{ number: phoneErrorMessage }]
    } else {
      const phoneNumberValidation = {
        [telephoneTypeIds.house]: regexPhone,
        [telephoneTypeIds.office]: regexPhone,
        [telephoneTypeIds.cellPhone]: regexCellPhone,
      }[telephoneType]

      if (phoneNumberValidation && !phoneNumberValidation.test(phoneNumber)) {
        errors.generalTelephone = [{ number: phoneErrorMessage }]
      }
    }
  }

  const validatePhoneNumberIntl = ({ values, errors }) => {
    const phoneNumber = getPhoneNumber(values)
    const telephoneTypeId = getTelephoneTypeId(values)
    const phoneErrorMessage = t('CREATE_CLIENT_T.INTRODUCE_VALID_PHONE_NUMBER')

    if (!phoneNumber || !validatePhone(phoneNumber, values)) {
      errors.generalTelephone = [{ number: phoneErrorMessage }]
      return
    }

    const numberType = parsePhoneNumber(
      phoneNumber,
      getCountryCode(values)
    ).getType()

    if (!numberType || !isValidNumberType(numberType, telephoneTypeId)) {
      errors.generalTelephone = [{ number: phoneErrorMessage }]
    }
  }

  // Auxiliary functions
  const getPhoneNumber = values => {
    return values.generalStore
      ? values.generalStore[0]?.generalTelephone[0]?.number
      : values.generalTelephone[0]?.number
  }

  const getTelephoneTypeId = values => {
    return values.generalStore
      ? values.generalStore[0]?.generalTelephone[0]?.telephoneTypeId?.toUpperCase()
      : values.generalTelephone[0]?.telephoneTypeId?.toUpperCase()
  }

  const getCountryCode = values => {
    return values.generalStore
      ? values.generalStore[0]?.generalTelephone[0]?.countryCode || 'PA'
      : values.generalTelephone[0]?.countryCode || 'PA'
  }

  const validatePhone = (phoneNumber, values) => {
    const parsedNumber = parsePhoneNumber(phoneNumber, getCountryCode(values))
    return parsedNumber?.isValid()
  }

  return {
    validatePhoneNumber,
    validateEmail,
    validateDocument,
    validateAddress,
    validateGenderId,
    validatePhoneNumberIntl,
  }
}
